import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from 'modules/common/components/Checkbox';
import Counter from 'modules/common/components/Counter';
import {
  invoiceGridItemsToSizeMap,
  orderInvoiceGridHeaderKeys,
  orderInvoiceGridHeaderList,
} from 'modules/orders/OrdersView/OrdersContainer/OrderDetailsContainer/OrderDetailsComponent/helper';
import { getInsuranceLineDiscountFromLookup } from 'modules/orders/medicalInsuranceDiscountCalculate';
import {
  getInvoiceCheckableRowKeys,
  isInvoiceRowZeroEffectiveUnit,
} from 'modules/orders/OrdersView/OrdersContainer/OrderDetailsContainer/OrderDetailsComponent/invoiceCheckboxZeroPrice';
import { insuranceStatusEnum } from 'modules/orders/constants';
import ordersStrings from 'modules/orders/strings';
import { formatInsuranceOrderLine1 } from '../../../insuranceOrderEprescriptionFormatters';
import {
  OrderDetailsInvoiceGridWrapper,
  OrderDetailsInvoiceGridHeader,
  OrderDetailsInvoiceGridItem,
  ItemImage,
  OrderDetailsInvoiceGridHeaderTitle,
  OrderDetailsInvoiceGridItemText,
  InvoiceItemPrescriptionMeta,
  InvoiceItemZeroPriceAsterisk,
  InvoiceItemZeroPriceHint,
  InvoiceItemInsuranceBadge,
  InvoiceItemMoveToAdditionalActionBtn,
  InvoiceDiscountPriceStack,
  InvoiceDiscountPriceBeforeStruck,
  InvoiceDiscountPriceAfter,
  extendGrayButtonStyle,
} from '../../OrderInvoice.style';

const INSURANCE_STATUS_DISPLAY_CONFIG = {
  [insuranceStatusEnum.Approved]: {
    label: ordersStrings.orderDetailsContent.insuranceStatusApprovedLabel,
    style: 'approved',
    onlyForInsuranceRows: false,
  },
  [insuranceStatusEnum.Rejected]: {
    label: ordersStrings.orderDetailsContent.insuranceStatusRejectedLabel,
    style: 'warning',
    onlyForInsuranceRows: true,
  },
  [insuranceStatusEnum.ApprovalRequiredNotSubmitted]: {
    label: ordersStrings.orderDetailsContent.insuranceStatusApprovalRequiredNotSubmittedLabel,
    style: 'warning',
    onlyForInsuranceRows: true,
  },
};

const InvoiceItemInsuranceStatus = ({ insuranceStatus, isInsuranceItem }) => {
  const statusConfig = INSURANCE_STATUS_DISPLAY_CONFIG[insuranceStatus];
  if (!statusConfig) return null;
  if (statusConfig.onlyForInsuranceRows && !isInsuranceItem) return null;

  return (
    <InvoiceItemInsuranceBadge $variant={statusConfig.style}>
      {statusConfig.label}
    </InvoiceItemInsuranceBadge>
  );
};

const renderInvoiceItemNameCell = (item, rowIndex, ctx) => {
  const { invoiceZeroPriceAlternativesHint, onMoveInsuranceItemToAdditional } = ctx;
  const dosageLine = formatInsuranceOrderLine1(item)?.trim();
  const zeroPriceRow = isInvoiceRowZeroEffectiveUnit(item);
  const insuranceStatus = Number(item?.insuranceStatus);
  const isInsuranceItem = item?.prescriptionKey != null;
  const canMoveToAdditional =
    isInsuranceItem &&
    (insuranceStatus === insuranceStatusEnum.Rejected ||
      insuranceStatus === insuranceStatusEnum.ApprovalRequiredNotSubmitted);

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        minWidth: 0,
      }}
    >
      <ItemImage src={item.mainImageUrl} alt={item.serviceName} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          minWidth: 0,
          justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <OrderDetailsInvoiceGridItemText style={{ margin: 0 }}>
            {item.serviceName}
          </OrderDetailsInvoiceGridItemText>
          <InvoiceItemInsuranceStatus
            insuranceStatus={insuranceStatus}
            isInsuranceItem={isInsuranceItem}
          />
          {zeroPriceRow ? (
            <InvoiceItemZeroPriceAsterisk aria-hidden>*</InvoiceItemZeroPriceAsterisk>
          ) : null}
        </div>
        {zeroPriceRow && invoiceZeroPriceAlternativesHint ? (
          <InvoiceItemZeroPriceHint>{invoiceZeroPriceAlternativesHint}</InvoiceItemZeroPriceHint>
        ) : null}
        {dosageLine ? (
          <InvoiceItemPrescriptionMeta>{dosageLine}</InvoiceItemPrescriptionMeta>
        ) : null}
        {canMoveToAdditional ? (
          <InvoiceItemMoveToAdditionalActionBtn
            type="button"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onMoveInsuranceItemToAdditional(item.serviceKey, rowIndex);
            }}
          >
            {ordersStrings.orderDetailsContent.insuranceStatusMoveToAdditionalActionLabel}
          </InvoiceItemMoveToAdditionalActionBtn>
        ) : null}
      </div>
    </div>
  );
};

const InvoiceTable = ({
  servicesList,
  bigGridItemPadding,
  handleServiceItemDeleteBtnClick,
  handleChangeItemQuantity,
  disableFlexGrow,
  enableEditInvoice,
  handleInvoiceItemCheckToBeConfirmed,
  checkedItemToBeConfirmed,
  disableTableBorders,
  insuranceRxLineDiscountLookup,
  invoiceZeroPriceAlternativesHint,
  onMoveInsuranceItemToAdditional,
}) => {
  const invoiceGridSize = Object.keys(invoiceGridItemsToSizeMap)
    .map(itemSizeKey => invoiceGridItemsToSizeMap[itemSizeKey])
    .join(' ');

  const nameCellCtx = {
    invoiceZeroPriceAlternativesHint,
    onMoveInsuranceItemToAdditional,
  };
  const checkableKeys = getInvoiceCheckableRowKeys(servicesList);
  const allCheckableSelected =
    checkableKeys.length > 0 && checkableKeys.every(k => checkedItemToBeConfirmed?.includes(k));

  return (
    <OrderDetailsInvoiceGridWrapper
      bigGridItemPadding={bigGridItemPadding}
      invoiceGridSize={invoiceGridSize}
      disableFlexGrow={disableFlexGrow}
      disableTableBorders={disableTableBorders}
    >
      {orderInvoiceGridHeaderKeys.map((orderInvoiceGridHeaderKey, index) => (
        <OrderDetailsInvoiceGridHeader
          key={orderInvoiceGridHeaderKey}
          pushToEnd={orderInvoiceGridHeaderKey === 'invoiceTotalGridHeader'}
        >
          {enableEditInvoice && index === 0 ? (
            <Checkbox
              checked={allCheckableSelected}
              disabled={checkableKeys.length === 0}
              key={orderInvoiceGridHeaderKey}
              handleCheckboxClick={() => {
                handleInvoiceItemCheckToBeConfirmed();
              }}
              checkboxObj={{
                key: orderInvoiceGridHeaderKey,
                name: (
                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                    }}
                  >
                    <OrderDetailsInvoiceGridHeaderTitle>
                      {orderInvoiceGridHeaderList[orderInvoiceGridHeaderKey]}
                    </OrderDetailsInvoiceGridHeaderTitle>
                  </div>
                ),
              }}
            />
          ) : (
            <OrderDetailsInvoiceGridHeaderTitle>
              {orderInvoiceGridHeaderList[orderInvoiceGridHeaderKey]}
            </OrderDetailsInvoiceGridHeaderTitle>
          )}
        </OrderDetailsInvoiceGridHeader>
      ))}

      {servicesList.map((item, index) => {
        const rowIndex = item.invoiceRowIndex !== undefined ? item.invoiceRowIndex : index;
        const lineDiscount = getInsuranceLineDiscountFromLookup(
          insuranceRxLineDiscountLookup,
          item.serviceKey,
          item.productShapeId,
          item.quantity,
        );
        const qty = item.quantity || 1;
        const unitPrice = Number(item.price ?? item.servicePrice ?? 0);
        const unitBeforeDiscount = lineDiscount ? lineDiscount.priceBefore : unitPrice;
        const unitAfterDiscount = lineDiscount ? lineDiscount.priceAfter : null;
        const rowZeroPrice = isInvoiceRowZeroEffectiveUnit(item);

        return (
          <React.Fragment key={`${item.serviceKey}-${item.productShapeId}-${rowIndex}`}>
            {enableEditInvoice ? (
              <OrderDetailsInvoiceGridItem displayFlex startPadding endPadding>
                <Checkbox
                  checked={checkedItemToBeConfirmed?.includes(
                    `${item.serviceKey}-${item.productShapeId}`,
                  )}
                  disabled={rowZeroPrice}
                  key={item.serviceKey}
                  handleCheckboxClick={() =>
                    handleInvoiceItemCheckToBeConfirmed(`${item.serviceKey}-${item.productShapeId}`)
                  }
                  checkboxObj={{
                    key: item.serviceKey,
                    name: renderInvoiceItemNameCell(item, rowIndex, nameCellCtx),
                  }}
                />
              </OrderDetailsInvoiceGridItem>
            ) : (
              <OrderDetailsInvoiceGridItem displayFlex startPadding endPadding>
                {renderInvoiceItemNameCell(item, rowIndex, nameCellCtx)}
              </OrderDetailsInvoiceGridItem>
            )}
            <OrderDetailsInvoiceGridItem>
              {lineDiscount ? (
                <InvoiceDiscountPriceStack>
                  <InvoiceDiscountPriceBeforeStruck>
                    {unitBeforeDiscount.toFixed(2)}
                  </InvoiceDiscountPriceBeforeStruck>
                  <InvoiceDiscountPriceAfter>
                    {(unitAfterDiscount != null ? unitAfterDiscount : unitPrice).toFixed(2)}
                  </InvoiceDiscountPriceAfter>
                </InvoiceDiscountPriceStack>
              ) : (
                <OrderDetailsInvoiceGridItemText>
                  {item?.price?.toFixed(2)}
                </OrderDetailsInvoiceGridItemText>
              )}
            </OrderDetailsInvoiceGridItem>
            <OrderDetailsInvoiceGridItem>
              <OrderDetailsInvoiceGridItemText>
                {enableEditInvoice ? (
                  <Counter
                    count={item.quantity}
                    extendButtonStyle={extendGrayButtonStyle}
                    key={item.serviceKey}
                    label={item.productShapeName}
                    onClickDecrease={() =>
                      handleChangeItemQuantity(item.serviceKey, rowIndex, 'minus')
                    }
                    onClickIncrease={() =>
                      handleChangeItemQuantity(item.serviceKey, rowIndex, 'plus')
                    }
                    onDelete={() => handleServiceItemDeleteBtnClick(rowIndex)}
                  />
                ) : (
                  `${item.quantity} ${item.productShapeName}`
                )}
              </OrderDetailsInvoiceGridItemText>
            </OrderDetailsInvoiceGridItem>

            <OrderDetailsInvoiceGridItem>
              {lineDiscount ? (
                <InvoiceDiscountPriceStack>
                  <InvoiceDiscountPriceBeforeStruck>
                    {(unitBeforeDiscount * qty).toFixed(2)}
                  </InvoiceDiscountPriceBeforeStruck>
                  <InvoiceDiscountPriceAfter>
                    {(unitAfterDiscount != null
                      ? unitAfterDiscount * qty
                      : unitPrice * qty
                    ).toFixed(2)}
                  </InvoiceDiscountPriceAfter>
                </InvoiceDiscountPriceStack>
              ) : (
                <OrderDetailsInvoiceGridItemText>
                  {(item.price * (item.quantity || 1)).toFixed(2)}
                </OrderDetailsInvoiceGridItemText>
              )}
            </OrderDetailsInvoiceGridItem>
          </React.Fragment>
        );
      })}
    </OrderDetailsInvoiceGridWrapper>
  );
};

InvoiceTable.propTypes = {
  disableTableBorders: PropTypes.bool,
  handleInvoiceItemCheckToBeConfirmed: PropTypes.func,
  checkedItemToBeConfirmed: PropTypes.arrayOf(PropTypes.string),
  enableEditInvoice: PropTypes.bool,
  disableFlexGrow: PropTypes.bool,
  orderDetails: PropTypes.shape({
    details: PropTypes.shape({
      orderItems: PropTypes.arrayOf({
        price: PropTypes.number,
      }),
    }),
  }),
  orderInvoiceGridHeaderList: PropTypes.shape({}).isRequired,
  servicesList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  invoiceGridItemsToSizeMap: PropTypes.shape({}).isRequired,
  bigGridItemPadding: PropTypes.bool,
  handleServiceItemDeleteBtnClick: PropTypes.func.isRequired,
  handleChangeItemQuantity: PropTypes.func.isRequired,
  insuranceRxLineDiscountLookup: PropTypes.objectOf(
    PropTypes.shape({
      priceBefore: PropTypes.number,
      priceAfter: PropTypes.number,
    }),
  ),
  invoiceZeroPriceAlternativesHint: PropTypes.string,
  onMoveInsuranceItemToAdditional: PropTypes.func,
};

InvoiceItemInsuranceStatus.propTypes = {
  insuranceStatus: PropTypes.number.isRequired,
  isInsuranceItem: PropTypes.bool.isRequired,
};

InvoiceTable.defaultProps = {
  bigGridItemPadding: false,
  insuranceRxLineDiscountLookup: null,
  invoiceZeroPriceAlternativesHint: '',
  onMoveInsuranceItemToAdditional: null,
};

export default InvoiceTable;
